import { useTranslation } from 'react-i18next';
import type { Role, UserRecord } from '../../../types';

interface UserTableProps {
  users: UserRecord[];
  isAdmin: boolean;
  currentUserId?: string;
  editingId: string | null;
  editForm: { name: string; email: string; role: Role };
  onEdit: (user: UserRecord) => void;
  onCancelEdit: () => void;
  onSave: (userId: string) => void;
  onDeleteClick: (userId: string, name: string) => void;
  onFormChange: (field: keyof { name: string; email: string; role: Role }, value: any) => void;
  formatDate: (dateStr?: string | null) => string;
}

export function UserTable({
  users,
  isAdmin,
  currentUserId,
  editingId,
  editForm,
  onEdit,
  onCancelEdit,
  onSave,
  onDeleteClick,
  onFormChange,
  formatDate,
}: UserTableProps) {
  const { t } = useTranslation();

  return (
    <div className="admin-table-responsive">
      <table className="admin-table">
        <thead>
          <tr>
            <th>{t('admin.userName')}</th>
            <th>{t('admin.userEmail')}</th>
            <th>{t('admin.userRole')}</th>
            <th>{t('admin.userProvider')}</th>
            <th>{t('admin.userJoined')}</th>
            {isAdmin && <th>{t('admin.actions')}</th>}
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.id} className={editingId === u.id ? 'editing' : ''}>
              <td>
                {editingId === u.id ? (
                  <input
                    className="admin-inline-input"
                    value={editForm.name}
                    onChange={(e) => onFormChange('name', e.target.value)}
                  />
                ) : (
                  <div className="admin-user-cell">
                    <span className="admin-user-avatar">{u.name?.charAt(0).toUpperCase()}</span>
                    {u.name}
                  </div>
                )}
              </td>
              <td>{u.email}</td>
              <td>
                {editingId === u.id ? (
                  <select
                    className="admin-inline-select"
                    value={editForm.role}
                    onChange={(e) => onFormChange('role', e.target.value as Role)}
                  >
                    <option value="USER">USER</option>
                    <option value="ADMIN">ADMIN</option>
                  </select>
                ) : (
                  <span className={`admin-role-badge role-${u.role.toLowerCase()}`}>{u.role}</span>
                )}
              </td>
              <td>
                <span className="admin-provider-cell">
                  {u.provider === 'google' ? (
                    <>
                      <svg viewBox="0 0 24 24" width="16" height="16">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                      </svg>
                      <span className="admin-provider-text">Google</span>
                    </>
                  ) : (
                    <>
                      <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                        <polyline points="22,6 12,13 2,6"/>
                      </svg>
                      <span className="admin-provider-text">Email</span>
                    </>
                  )}
                </span>
              </td>
              <td>{formatDate(u.createdAt)}</td>
              {isAdmin && (
                <td>
                  {u.id === currentUserId ? (
                    <span className="admin-you-badge">{t('admin.you')}</span>
                  ) : editingId === u.id ? (
                    <div className="admin-action-btns">
                      <button className="admin-btn admin-btn-save" onClick={() => onSave(u.id)}>
                        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                      </button>
                      <button className="admin-btn admin-btn-cancel" onClick={onCancelEdit}>
                        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                      </button>
                    </div>
                  ) : (
                    <div className="admin-action-btns">
                      <button className="admin-btn admin-btn-edit" onClick={() => onEdit(u)}>
                        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                      </button>
                      <button className="admin-btn admin-btn-delete" onClick={() => onDeleteClick(u.id, u.name)}>
                        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
                      </button>
                    </div>
                  )}
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
